import { autoUpdate, computePosition } from '@floating-ui/dom'
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'
import { render, unmountComponentAtNode } from 'react-dom'

import { tablePluginKey as TableRightClickMenuPluginKey } from 'common/Prose/Plugins/TableRightClickMenu/TableRightClickMenu'
import { CellSelection } from 'common/Prose/utils/table/CellSelection'
import { AlmanacEditorState, AlmanacEditorStateContext } from 'stores/EditorState'
import { TableFormatBar } from '../components/TableFormatBar/TableFormatBar'
import Styles from './CellFormatBarPlugin.module.scss'
import { QueryClientProvider } from 'react-query'
import queryClient from 'queryClient'
import { Provider } from 'react-redux'
import reduxStore from 'redux/store'

interface PluginState {
  decoration: Decoration | null
  wrapper: HTMLElement | null
}

const VIEWPORT_MARGIN = 16

let autoUpdateCleanup: ReturnType<typeof autoUpdate> | null = null

export const CellFormatBarPluginKey = new PluginKey('CellFormatBar')
export const CellFormatBarPlugin = new Plugin({
  key: CellFormatBarPluginKey,
  props: {
    decorations: (editorState) => {
      const { decoration }: PluginState = CellFormatBarPluginKey.getState(editorState)
      if (!decoration) return

      return DecorationSet.create(editorState.doc, [decoration])
    },
  },
  state: {
    apply: (tr, pluginState, oldEditorState, newEditorState): PluginState => {
      // NOTE: if the format bar should be hidden
      if (
        TableRightClickMenuPluginKey.getState(newEditorState)?.active ||
        !(newEditorState.selection instanceof CellSelection)
      ) {
        const { wrapper } = pluginState
        if (wrapper) unmountComponentAtNode(wrapper)
        if (autoUpdateCleanup) {
          autoUpdateCleanup()
          autoUpdateCleanup = null
        }

        return { decoration: null, wrapper: null }
      }

      // NOTE: if the doc didn't change and the format bar didn't become active
      // either
      if (oldEditorState.selection.eq(newEditorState.selection) && !tr.docChanged) return pluginState

      // NOTE: in all other cases, construct a new decoration so react has a
      // chance to re-render (or initially render)
      const { wrapper: existingWrapper }: PluginState = CellFormatBarPluginKey.getState(oldEditorState)
      const wrapper = existingWrapper ?? document.createElement('div')
      if (!existingWrapper) {
        wrapper.classList.add(Styles.wrapper)
        // NOTE: Not sure why this is necessary, but without it the bar
        // disappears, because prosemirror detects dom mutations?
        wrapper.addEventListener('mousedown', (e) => e.preventDefault())
      }

      return {
        decoration: Decoration.widget(
          newEditorState.selection.$fromCell.before(-1),
          (view) => {
            // NOTE: wait for the table to update its dimensions
            setTimeout(() => {
              const cellSelection = view.state.selection
              if (!(cellSelection instanceof CellSelection)) return

              const tableElement = view.domAtPos(cellSelection.$fromCell.before(-1) + 1)?.node?.parentElement as
                | HTMLElement
                | undefined
              const fromCellElement = view.domAtPos(cellSelection.$fromCell.pos + 1)?.node as HTMLElement | undefined
              const toCellElement = view.domAtPos(cellSelection.$toCell.pos + 1)?.node as HTMLElement | undefined

              if (fromCellElement && toCellElement && tableElement) {
                autoUpdateCleanup = autoUpdate(fromCellElement, wrapper, async () => {
                  const { x: left, y } = await computePosition(fromCellElement, wrapper, { placement: 'top-start' })
                  const { x: right } = await computePosition(toCellElement, wrapper, { placement: 'right' })

                  // NOTE: this is the scroll container of the table
                  const tableWrapper = tableElement.parentElement?.parentElement
                  if (!tableWrapper) return

                  const viewportWidth = document.body.getBoundingClientRect().width

                  // NOTE: clamp to right (Math.min) and left (Math.max) table
                  // wrapper edges (with a margin of half the format bar width)
                  // so the format bar doesn't show up way outside of the
                  // editor. As clamp to the left and right viewport edges with
                  // a margin and space for the right rail.
                  const targetLeft = Math.min(
                    Math.max(
                      (left + right) / 2,
                      tableWrapper.offsetLeft,
                      -tableWrapper.getBoundingClientRect().left + wrapper.offsetWidth / 2 + VIEWPORT_MARGIN,
                    ),
                    tableWrapper.offsetLeft + tableWrapper.offsetWidth,
                    viewportWidth -
                      parseInt(Styles.rightRailWidth) -
                      tableWrapper.getBoundingClientRect().left -
                      wrapper.offsetWidth / 2 -
                      VIEWPORT_MARGIN,
                  )

                  // NOTE: If the cell selection isn't visible anymore -> hide
                  // the format bar
                  const hideFormatBar =
                    left > tableWrapper.offsetLeft + tableWrapper.offsetWidth ||
                    right + tableElement.offsetLeft < tableWrapper.offsetLeft

                  Object.assign(wrapper.style, {
                    left: `${targetLeft}px`,
                    top: `${y}px`,
                    opacity: hideFormatBar ? '0' : '1',
                  })
                })
              }
            })

            const editorStateStore = new AlmanacEditorState(view)

            render(
              <AlmanacEditorStateContext.Provider value={editorStateStore}>
                <Provider store={reduxStore}>
                  <QueryClientProvider client={queryClient}>
                    <TableFormatBar view={view} />
                  </QueryClientProvider>
                </Provider>
              </AlmanacEditorStateContext.Provider>,
              wrapper,
            )

            return wrapper
          },
          { stopEvent: () => true },
        ),
        wrapper,
      }
    },
    init: (): PluginState => ({ decoration: null, wrapper: null }),
  },
})
